import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Question } from '../types/quiz';
import { useLanguage } from '../contexts/LanguageContext';

interface QuizResultsProps {
  questions: Question[];
  userAnswers: number[][];
  timeElapsed: number;
  onRestart: () => void;
}

export default function QuizResults({
  questions,
  userAnswers,
  timeElapsed,
  onRestart
}: QuizResultsProps) {
  const { t } = useLanguage();
  
  // Filter only attempted questions
  const attemptedQuestions = questions.filter((_, index) => userAnswers[index].length > 0);
  const attemptedAnswers = userAnswers.filter(answer => answer.length > 0);

  // Calculate scores only for attempted questions
  const results = attemptedQuestions.map((question, index) => {
    const userAnswer = attemptedAnswers[index];
    const isCorrect = userAnswer.length === question.correctAnswers.length &&
      userAnswer.every(ans => question.correctAnswers.includes(ans)) &&
      question.correctAnswers.every(ans => userAnswer.includes(ans));
    
    return {
      question,
      userAnswer,
      isCorrect,
      marks: isCorrect ? question.marks : 0
    };
  });

  const totalMarks = results.reduce((sum, result) => sum + result.question.marks, 0);
  const earnedMarks = results.reduce((sum, result) => sum + result.marks, 0);
  const correctAnswers = results.filter(result => result.isCorrect).length;
  const percentage = Math.round((earnedMarks / totalMarks) * 100);

  const minutes = Math.floor(timeElapsed / 60);
  const seconds = timeElapsed % 60;

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <Trophy className="w-10 h-10 text-purple-600" />
        </motion.div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          {t('quiz.completed')}
        </h2>
        <p className="text-gray-600">
          {t('quiz.resultsSummary')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <p className="text-sm text-purple-600 mb-1">{t('quiz.accuracy')}</p>
          <p className="text-3xl font-bold text-purple-700">{percentage}%</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-sm text-green-600 mb-1">{t('quiz.score')}</p>
          <p className="text-3xl font-bold text-green-700">
            {earnedMarks}/{totalMarks}
          </p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-sm text-blue-600 mb-1">{t('quiz.timeTaken')}</p>
          <p className="text-3xl font-bold text-blue-700">
            {minutes}:{seconds.toString().padStart(2, '0')}
          </p>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4">{t('quiz.detailedResults')}</h3>
        <div className="space-y-4">
          {results.map((result, index) => {
            return (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 mb-2">
                      {index + 1}. {result.question.text}
                    </p>
                    <div className="text-sm text-gray-600">
                      <p className="mb-1">
                        {t('quiz.yourAnswer')}: {result.userAnswer.map(ans => result.question.options[ans]).join(', ')}
                      </p>
                      <p>
                        {t('quiz.correctAnswer')}: {result.question.correctAnswers.map(ans => result.question.options[ans]).join(', ')}
                      </p>
                    </div>
                  </div>
                  <div className="ml-4">
                    {result.isCorrect ? (
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={onRestart}
          className="bg-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
        >
          {t('quiz.tryAgain')}
        </button>
      </div>
    </div>
  );
}
